import { create } from 'zustand'
import { nanoid } from 'nanoid'
import { getOccupiedCells } from '../utils/rotation'
import { clearPlantersFromCells, isFrame } from '../utils/planHelpers'

export const useFurnitureStore = create(set => ({
    // Schema: see frontend/src/components/settings/drawer/furnitureVariants.js
    selectedVariant: null,

    // Schema: see frontend/src/utils/schemas.js
    placedItems: [],

    clearPlacedItems: () => set({ placedItems: [] }),

    // Schema: mapping from instance id to objects containing property: message.
    validationErrors: {},

    setSelectedVariant: variant => set({ selectedVariant: variant }),
    resetVariant:      () => set({ selectedVariant: null }),

    addPlacedItem: (item, plan = null, setPlan = null) => {
        const { row, col, variant } = item
        const id = nanoid()
        // prendo la rotation che ho passato (ghostRotation), oppure il primo stato
        const initialRot    = item.rotation ?? item.variant.rotationStates?.[0]
        const horiz         = initialRot === 't' || initialRot === 'b'
        // calcolo widthCell/heightCell in base alla rotazione iniziale
        const widthCell     = horiz ? item.variant.widthCell  : item.variant.heightCell
        const heightCell    = horiz ? item.variant.heightCell : item.variant.widthCell

        // Se stiamo posizionando un telaio con ruote, rimuoviamo eventuali fioriere nelle celle occupate
        if (isFrame(item.variant) && plan && setPlan) {
            // getOccupiedCells applica la rotazione, quindi passa i valori NON ruotati dal variant
            const occupiedCells = getOccupiedCells(row, col, item.variant.widthCell, item.variant.heightCell, initialRot)
            const newPlan = clearPlantersFromCells(plan, occupiedCells)
            setPlan(newPlan)
        }

        if (item.variant.category === 'domino_lights') {
            set(state => {
                const existing = state.placedItems.find(p =>
                    p.variant.id === item.variant.id &&
                    p.row === item.row &&
                    p.col === item.col
                )
                if (existing) {
                    const nextCount = existing.count < 4 ? existing.count + 1 : 0
                    if (nextCount === 0) {
                        return { placedItems: state.placedItems.filter(p => p.instanceId !== existing.instanceId) }
                    }
                    return {
                        placedItems: state.placedItems.map(p =>
                            p.instanceId === existing.instanceId ? { ...p, count: nextCount } : p
                        )
                    }
                }
                return {
                    placedItems: [
                        ...state.placedItems,
                        { row, col, variant, rotation: initialRot, rotationStates: variant.rotationStates,
                            widthCell, heightCell, instanceId: id, count: 1 }
                    ]
                }
            })
            return
        }

        set(state => ({
            placedItems: [
                ...state.placedItems,
                {
                    ...item,
                    rotation:       initialRot,
                    rotationStates: item.variant.rotationStates,
                    widthCell,
                    heightCell,
                    instanceId:     id,
                }
            ]
        }))
    },

    resetPlacedItems: (furnishings) => {
        console.debug('resetPlacedItems(furnishings)', {furnishings})
        set(state => ({
            selectedVariant: null,
            placedItems:     furnishings,
            validationErrors:{},
        }))
    },
    decrementDominoLight: ({ row, col, variantId }) => {
        set(state => {
            const existing = state.placedItems.find(p =>
                p.variant.id === variantId &&
                p.row === row &&
                p.col === col
            )
            if (!existing) return {}
            const nextCount = existing.count - 1
            if (nextCount <= 0) {
                return { placedItems: state.placedItems.filter(p => p.instanceId !== existing.instanceId) }
            }
            return {
                placedItems: state.placedItems.map(p =>
                    p.instanceId === existing.instanceId ? { ...p, count: nextCount } : p
                )
            }
        })
    },

    updatePlacedItem: (instanceId, updates) =>
        set(state => ({
            placedItems: state.placedItems.map(p =>
                p.instanceId !== instanceId
                    ? p
                    : {
                        ...p,
                        ...updates,
                        // se aggiorno la rotation, riallineo widthCell/heightCell
                        ...(updates.rotation && (() => {
                            const horiz = updates.rotation === 't' || updates.rotation === 'b'
                            return {
                                widthCell:  horiz ? p.variant.widthCell  : p.variant.heightCell,
                                heightCell: horiz ? p.variant.heightCell : p.variant.widthCell,
                            }
                        })())
                    }
            )
        })),

    removePlacedItem: instanceId =>
        set(state => ({
            placedItems: state.placedItems.filter(p => p.instanceId !== instanceId)
        })),

    setValidationError: (instanceId, errorObject) =>
        set(state => ({
            validationErrors: {
                ...state.validationErrors,
                [instanceId]: errorObject
            }
        })),
    clearValidationErrors: () =>
        set({ validationErrors: {} })
}))
